/**
 * Signed-in notice bar (under the navbar) for pending team invitations.
 *
 * Notices only link to Account, where the invitee accepts or declines. Dismissal is saved in
 * Composer per invite, so it also hides the notice in the desktop app.
 */

"use client";

import React, { useCallback, useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Box } from "@mui/material";
import { TeamInviteNotices, mapTeamInvitesForMe } from "@neuraville/react-core-pro/subscription";
import { useAuth } from "../../util/auth";
import { fetchMyTeamInvites, teamDismissInviteNotice } from "../../api/stripe";
import { TEAM_INVITE_SETTINGS_PATH, TEAM_MEMBERSHIP_CHANGED_EVENT } from "../../util/teamMembershipEvents";

/** Hidden on auth screens and on Account, which already shows the invitation card. */
export function shouldShowTeamInviteBar(pathname) {
  const path = typeof pathname === "string" ? pathname : "";
  return !path.startsWith("/auth") && !path.startsWith(TEAM_INVITE_SETTINGS_PATH);
}

export default function TeamInviteNoticeBar() {
  const { user } = useAuth();
  const accessToken = user?.accessToken ?? null;
  const pathname = usePathname();
  const router = useRouter();
  const [invites, setInvites] = useState([]);

  const load = useCallback(async () => {
    if (!accessToken) {
      setInvites([]);
      return;
    }
    try {
      setInvites(mapTeamInvitesForMe(await fetchMyTeamInvites(accessToken)));
    } catch (err) {
      console.error("[TeamInviteNoticeBar] Could not load team invites:", err);
      setInvites([]);
    }
  }, [accessToken]);

  useEffect(() => {
    void load();
    const onChanged = () => void load();
    window.addEventListener(TEAM_MEMBERSHIP_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(TEAM_MEMBERSHIP_CHANGED_EVENT, onChanged);
  }, [load]);

  const dismiss = useCallback(
    async (inviteId) => {
      await teamDismissInviteNotice(accessToken, inviteId);
      setInvites((prev) =>
        prev.map((inv) => (inv.invite_id === inviteId ? { ...inv, notice_dismissed: true } : inv))
      );
    },
    [accessToken]
  );

  const hasVisibleNotice = invites.some((inv) => !inv.notice_dismissed);
  if (!accessToken || !hasVisibleNotice || !shouldShowTeamInviteBar(pathname)) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", mx: "auto", px: 2, pt: 2 }}>
      <TeamInviteNotices
        invites={invites}
        onDismiss={dismiss}
        onViewInvitation={() => router.push(TEAM_INVITE_SETTINGS_PATH)}
      />
    </Box>
  );
}
